import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Chart, enqueueSnackbar, FormProvider, RHFToggleButtonGroup, Spinner, useChart } from '@zeta-core';
import { FinancialMisService, getErrorMessage } from '@zeta-services';
import { IEmptyRequestResponse, IGenericErrorResponse } from '@zeta-types';

export const CurrentInvestment = ({ clientID, isClient }: { clientID?: string; isClient?: boolean }) => {
  const [graphData, setGraphData] = useState([]);
  const theme = useTheme();

  const methods = useForm({
    defaultValues: {
      frequency: '3m',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  // In your component, get the current frequency
  const currentFrequency = methods.watch('frequency');

  const portfolioPerformance = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getPortfolioGrowth(payload) : FinancialMisService().getPortfolioGrowthAdvisor(payload),
    onSuccess: (response) => {
      setGraphData(response?.data);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    const getDateRange = (frequency) => {
      const toDate = new Date();
      const fromDate = new Date();

      switch (frequency) {
        case '3m':
          fromDate.setMonth(fromDate.getMonth() - 3);
          break;
        case '6m':
          fromDate.setMonth(fromDate.getMonth() - 6);
          break;
        case '1y':
          fromDate.setFullYear(fromDate.getFullYear() - 1);
          break;
        case '3y':
          fromDate.setFullYear(fromDate.getFullYear() - 3);
          break;
        case '5y':
          fromDate.setFullYear(fromDate.getFullYear() - 5);
          break;
        default:
          fromDate.setMonth(fromDate.getMonth() - 3);
      }

      return {
        fromDate: fromDate.toISOString().split('T')[0],
        toDate: toDate.toISOString().split('T')[0],
      };
    };

    const dateRange = getDateRange(currentFrequency);
    const payload = {
      clientID,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    };

    if (clientID) {
      portfolioPerformance?.mutateAsync(payload);
    }
  }, [currentFrequency, clientID]); // Add currentFrequency as dependency

  const series = [
    {
      name: 'Portfolio Value',
      data: graphData?.portfolioGrowth?.map((item) => item?.currentHoldingValue),
    },
  ];

  const chartOptions = useChart({
    colors: [theme.palette.primary.main],
    fill: {
      gradient: {
        type: 'vertical',
        shadeIntensity: 0,
        opacityFrom: 0.4,
        opacityTo: 0,
        stops: [0, 100],
      },
    },
    xaxis: {
      categories: graphData?.portfolioGrowth?.map((item) => item.valuationDate),
      labels: {
        show: false,
        style: {
          colors: theme.palette.text.secondary,
          fontSize: '12px',
        },
        formatter: (value) => {
          const date = dayjs(value);
          if (['3m', '6m', '1y'].includes(currentFrequency)) {
            return date.format('MMM YY'); // Show 'Jan', 'Feb', etc.
          } else {
            return date.format('MMM YYYY'); // Show 'Jan 2024', 'Feb 2024'
          }
        },
        rotate: -45,
        maxHeight: 100,
      },
      tickAmount: 6, // Adjusts spacing between labels
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: undefined,
      max: undefined,
      tickAmount: graphData?.portfolioGrowth?.length > 5 ? 5 : 9,
      labels: {
        formatter: (value) => {
          if (value >= 1000000) return `£${(value / 1000000).toFixed(3)}M`;
          if (value >= 1000) return `£${(value / 1000).toFixed(3)}k`;
          return value.toFixed(0);
        },
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
    grid: {
      strokeDashArray: 3,
    },
    stroke: {
      width: 2,
      curve: 'straight',
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        show: true,
        formatter: (value, { dataPointIndex }) => {
          const dateValue = graphData?.portfolioGrowth?.[dataPointIndex]?.valuationDate;
          return dateValue ? dayjs(dateValue).format('DD MMM YYYY') : '';
        },
      },
      y: {
        formatter: (value) => `£${(value / 1000).toFixed(2)}k`, // Retain existing currency formatting
      },
    },
  });

  return (
    <Stack sx={{ mt: -9 }}>
      <Typography variant="subtitle1" sx={{ my: 2, display: 'flex', alignItems: 'center', pl: 2 }}>
        Portfolio Performance
      </Typography>
      <Stack px={2}>
        <FormProvider methods={methods}>
          <Grid container>
            <Grid
              size={{
                lg: 3
              }}>
              <RHFToggleButtonGroup
                primary
                size="small"
                name="frequency"
                options={[
                  {
                    label: '3 Months',
                    value: '3m',
                    color: 'primary',
                  },
                  {
                    label: '6 Months',
                    value: '6m',
                    color: 'primary',
                  },
                  {
                    label: '1 Year',
                    value: '1y',
                    color: 'primary',
                  },
                  {
                    label: '3 Years',
                    value: '3y',
                    color: 'primary',
                  },
                  {
                    label: '5 Years',
                    value: '5y',
                    color: 'primary',
                  },
                ]}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Stack>
      {portfolioPerformance?.isPending && (
        <Stack alignItems="center" justifyContent="center" minHeight="30vh">
          <Spinner />
        </Stack>
      )}
      {!portfolioPerformance?.isPending && series?.[0]?.data?.length > 0 && (
        <Chart dir="ltr" type="area" series={series} options={chartOptions} width="100%" height={320} />
      )}
      {!portfolioPerformance?.isPending && !series?.[0]?.data?.length && (
        <Stack alignItems="center" justifyContent="center" minHeight="20vh">
          <Typography variant="subtitle2">No Data</Typography>
        </Stack>
      )}
    </Stack>
  );
};
